import { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../../lib/cn';
import { StarSolidGlyph } from './icons';

/**
 * ListingRow.
 *
 * CSS selector: `organisms.css .ds-listing-row` with `__top`, `__store`,
 * `__rating`, `__price`, `__meta`, `__stock`, `__actions`.
 *
 * Doctrine:
 *
 * - This is the one place a store name IS the point, because the row exists so
 *   a buyer can choose between sellers of the same card. Product cards never
 *   carry a store name, this row always does.
 * - The klarna-04 invert: the store name is 15/600 ink and the price is 15/500
 *   secondary. PRICE IS LIGHTER THAN THE TITLE, not heavier.
 * - Hairline divided BY DESIGN, which is the deliberate contrast with
 *   `OrderRow`, where whitespace separates instead.
 * - There are no PSA / BGS / CGC grades. `conditionTags` carries real condition
 *   abbreviations, pass `.ds-tag--abbrev` atoms.
 * - No "Make an offer" and no "Message seller" slots exist, and none should be
 *   added. The product has neither feature.
 * - The add action is not baked in. Compose the CTA atom
 *   (`ds-cta ds-cta--ghost ds-cta--pill ds-cta--sm`) into `actions`, alongside
 *   the quantity stepper molecule.
 */
export const ListingRow = forwardRef(function ListingRow(
  {
    className,
    store,
    storeTo,
    /* See `SellerCell`: the origin, so the store can point back here. */
    storeState,
    to,
    /* Router `state` for the row's own destination. The stretched link below
       is the whole reason it is here: a page reached through this row may need
       to know where the reader was before it, and a plain `to` cannot say. */
    toState,
    openLabel,
    rating,
    price,
    conditionTags,
    stock,
    actions,
    children,
    ...rest
  },
  ref,
) {
  return (
    <div ref={ref} className={cn('ds-listing-row', to && 'ds-listing-row--open', className)} {...rest}>
      {/* THE STRETCHED LINK. The row carries a quantity stepper and two
          buttons, so it cannot itself BE a button — that is a control wrapping
          controls, which is invalid and which an audit already caught once on
          the product card. Instead the whole-row destination is a real <a>
          covering the row underneath everything else: `.ds-listing-row__open`
          is absolutely positioned at z-index 0 and the three content blocks sit
          at z-index 1, so a press on the stepper reaches the stepper and a
          press on the empty space between them reaches the listing. It is a
          link, so middle-click and "open in new tab" work, and it carries the
          only accessible name for that destination. */}
      {to ? (
        <Link
          className="ds-listing-row__open"
          to={to}
          state={toState}
          aria-label={openLabel || 'View listing'}
        />
      ) : null}
      <div className="ds-listing-row__top">
        <div>
          {/* Two destinations live in this row. The store name is the one that
              means the SHOP; everything else on it means this listing. */}
          <span className="ds-listing-row__store">
            {storeTo ? (
              <Link className="ds-listing-row__storelink" to={storeTo} state={storeState}>
                {store}
              </Link>
            ) : (
              store
            )}
          </span>
          {rating !== undefined && rating !== null && (
            <span className="ds-listing-row__rating">
              <StarSolidGlyph />
              {rating}
            </span>
          )}
        </div>
        {price && <span className="ds-listing-row__price">{price}</span>}
      </div>

      {(conditionTags || stock) && (
        <div className="ds-listing-row__meta">
          {conditionTags}
          {stock && <span className="ds-listing-row__stock">{stock}</span>}
        </div>
      )}

      {actions && <div className="ds-listing-row__actions">{actions}</div>}
      {children}
    </div>
  );
});

export default ListingRow;
